import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Cloud, CloudRain, CloudSnow, Sun, CloudSun, Wind, Loader2 } from 'lucide-react';
import { GlassCard } from './SharedUI';

interface DateWeatherWidgetProps {
  t: any;
  lang: string;
}

// Open-Meteo weathercode → 图标 + 中英文描述（免 API key、免费）
function mapWeatherCode(code: number, lang: string): { icon: any; label: string } {
  const zh = lang === 'zh';
  if (code === 0) return { icon: Sun, label: zh ? '晴' : 'Clear' };
  if (code <= 2) return { icon: CloudSun, label: zh ? '多云' : 'Partly cloudy' };
  if (code === 3) return { icon: Cloud, label: zh ? '阴' : 'Overcast' };
  if (code >= 45 && code <= 48) return { icon: Wind, label: zh ? '雾' : 'Fog' };
  if (code >= 51 && code <= 67) return { icon: CloudRain, label: zh ? '小雨' : 'Drizzle' };
  if (code >= 71 && code <= 77) return { icon: CloudSnow, label: zh ? '雪' : 'Snow' };
  if (code >= 80 && code <= 82) return { icon: CloudRain, label: zh ? '阵雨' : 'Rain' };
  if (code >= 95) return { icon: CloudRain, label: zh ? '雷雨' : 'Thunderstorm' };
  return { icon: Cloud, label: zh ? '未知' : 'Unknown' };
}

function buildCalendar(base: Date): (number | null)[] {
  const year = base.getFullYear();
  const month = base.getMonth();
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

export function DateWeatherWidget({ t, lang }: DateWeatherWidgetProps) {
  const zh = lang === 'zh';
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState<{ temp: number; code: number; city?: string } | null>(null);
  const [wxLoading, setWxLoading] = useState(true);

  // 每分钟刷新时间
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // 天气：浏览器定位 → open-meteo；失败静默降级（只显示日历）
  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async (lat: number, lon: number) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code`,
        );
        if (!res.ok) throw new Error('weather http ' + res.status);
        const data = await res.json();
        if (cancelled) return;
        setWeather({
          temp: Math.round(data?.current?.temperature_2m ?? 0),
          code: Number(data?.current?.weather_code ?? -1),
        });
      } catch {
        if (!cancelled) setWeather(null);
      } finally {
        if (!cancelled) setWxLoading(false);
      }
    };

    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        // 定位失败/拒绝 → 用北京兜底
        () => fetchWeather(39.9042, 116.4074),
        { timeout: 6000, maximumAge: 30 * 60 * 1000 },
      );
    } else {
      fetchWeather(39.9042, 116.4074);
    }
    return () => { cancelled = true; };
  }, []);

  const weekDays = zh ? ['日', '一', '二', '三', '四', '五', '六'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const monthLabel = zh
    ? `${now.getFullYear()}年${now.getMonth() + 1}月`
    : now.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  const cells = buildCalendar(now);
  const today = now.getDate();
  const timeStr = now.toLocaleTimeString(zh ? 'zh-CN' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  const weekdayStr = now.toLocaleDateString(zh ? 'zh-CN' : 'en-US', { weekday: 'long' });

  const wx = weather ? mapWeatherCode(weather.code, lang) : null;
  const WxIcon = wx?.icon;

  return (
    <GlassCard className="p-5 rounded-[2rem] border-white/5 bg-black/30 backdrop-blur-3xl">
      {/* 顶部：时间 + 天气 */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-3xl font-black text-white tracking-tighter leading-none">{timeStr}</div>
          <div className="mt-1 text-xs font-black uppercase tracking-widest text-white/45">{weekdayStr}</div>
        </div>
        <div className="text-right">
          {wxLoading ? (
            <Loader2 size={20} className="text-white/30 animate-spin ml-auto" />
          ) : wx && WxIcon ? (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-end"
            >
              <div className="flex items-center gap-1.5 text-cyan-300">
                <WxIcon size={22} />
                <span className="text-2xl font-black text-white tracking-tighter">{weather!.temp}°</span>
              </div>
              <span className="text-[11px] font-black uppercase tracking-widest text-white/40">{wx.label}</span>
            </motion.div>
          ) : (
            <span className="text-[11px] font-black uppercase tracking-widest text-white/25">
              {zh ? '天气不可用' : 'Weather N/A'}
            </span>
          )}
        </div>
      </div>

      {/* 月份标题 */}
      <div className="mb-2 text-xs font-black uppercase tracking-widest text-white/55">{monthLabel}</div>

      {/* 星期表头 */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekDays.map((d, i) => (
          <div key={`wd-${i}`} className="text-center text-[10px] font-black text-white/30 uppercase">{d}</div>
        ))}
      </div>

      {/* 日期网格 */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => (
          <div
            key={`d-${i}`}
            className={`aspect-square flex items-center justify-center text-xs font-bold rounded-lg transition-colors ${
              day === null
                ? ''
                : day === today
                  ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg'
                  : 'text-white/55 hover:bg-white/5'
            }`}
          >
            {day ?? ''}
          </div>
        ))}
      </div>
    </GlassCard>
  );
}
